import { cx } from '@twind/core';
import MagnifyingGlassIcon from 'app/components/icons/MagnifyingGlassIcon';
import SearchArrowIcon from 'app/components/icons/SearchArrowIcon';
import useUnityMode, { UnityMode } from 'app/hooks/useUnityMode';
import { useTranslation } from 'react-i18next';

export default function IntegratedSearchInput({
  value,
  onChange,
  onClickSearch,
  placeholder,
  disableWhenEmpty = false,
  className,
  displayClearButton = false,
  disabled = false,
}: {
  value: string;
  onChange: (value: string) => void;
  onClickSearch: (search: string) => void;
  placeholder?: string;
  disableWhenEmpty?: boolean;
  className?: string;
  displayClearButton?: boolean;
  disabled?: boolean;
}) {
  const unityMode = useUnityMode();
  const { t } = useTranslation();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (disableWhenEmpty && !value) {
      return;
    }

    e.preventDefault();
    onClickSearch(value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleClearClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.preventDefault();
    onChange('');
  };

  return (
    <div
      id="ostro-integrated-search-container"
      className={cx('relative w-full', className)}
    >
      <form
        onSubmit={handleSubmit}
        className="flex items-center border border-[#1E1E1E] rounded-[4px] overflow-hidden mb-0"
      >
        <input
          id="ostro-integrated-search-input"
          data-testid="ostro-integrated-search-input"
          className={`
            flex-grow
            h-[50px]
            p-4
            text-sm
            outline-none
            text-typography-secondary
            placeholder:text-typography-placeholder
            focus:placeholder:text-transparent
            [&::-webkit-search-cancel-button]:appearance-none
            [&::-webkit-search-decoration]:appearance-none
            bg-white
          `}
          placeholder={placeholder}
          value={value}
          onChange={handleInputChange}
          type="search"
        />
        {displayClearButton ? (
          <input
            value={t('clear_button')}
            type="reset"
            className="text-sm capitalize p-[13px] cursor-pointer"
            onClick={handleClearClick}
          />
        ) : (
          <button
            data-testid="ostro-integrated-search-button"
            id="ostro-integrated-search-button"
            className="bg-white h-[50px] px-4 flex items-center justify-center"
            aria-label="Search"
            type="submit"
            disabled={(disableWhenEmpty && !value) || disabled}
          >
            {unityMode === UnityMode.SEARCH_ONLY ? <MagnifyingGlassIcon /> : <SearchArrowIcon />}
          </button>
        )}
      </form>
    </div>
  );
}
